import { createRoute } from "@tanstack/react-router";
import { DashboardScreen } from "@/app/Dashboard/Screens/DashboardScreen";
import { appLayoutRoute } from "@/routes/layout-routes";
import {
  BookingStatus,
  ComparisonMode,
  QuerySupplierOfficesOrderByColumn,
  QueryVehiclesOrderByColumn,
  RelocationStatus,
  RelocationType,
  SortOrder,
} from "@/gql/graphql";
import { TimelineFilterOption } from "@/gql/schema";
import { prefetchQuery } from "@/lib/GraphQLCodegen/fetcher";
import { adminDashboardStatsQuery } from "@/app/Dashboard/GraphQL/adminDashboardStatsQuery";
import { toDateTimeTzString } from "@/lib/Formatters/toDateTimeString";
import { getDatesFromFilterOption } from "@/app/Dashboard/Utils/filterOptions";
import { bookingListQuery } from "@/app/Bookings/GraphQL/bookingListQuery";
import {
  bookingTableDefaultSorting,
  getBookingTableVariables,
} from "@/app/Dashboard/Utils/getBookingTableVariables";
import { createQueryVariables } from "@/lib/Components/DataTable/utils/data-table-utils";
import {
  getRelocationTableVariables,
  relocationTableDefaultSorting,
} from "@/app/Dashboard/Utils/getRelocationTableVariables";
import { relocationListQuery } from "@/app/Relocations/GraphQL/relocationListQuery";
import { relocationStatusCountsQuery } from "@/app/Relocations/GraphQL/relocationStatusCountsQuery";
import { supplierOfficeListQuery } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { vehicleListQuery } from "@/app/Vehicles/GraphQL/vehicleListQuery";
import { cityListQuery } from "@/app/Cities/Cities/GraphQL/cityListQuery";
import {
  workOrderRegionalCountsQuery,
  workOrderStatusCountQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/WorkOrderStatusCountQuery";
import { supplierParamToId } from "@/app/Suppliers/Utils/globalSupplier";

export const dashboardRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: "/",
  component: DashboardScreen,
  head: () => ({
    meta: [
      {
        title: "Dashboard - Imoova Admin",
      },
      {
        name: "description",
        content: "View bookings, relocations, and dashboard statistics",
      },
    ],
  }),
  loader: async ({ params }) => {
    const supplierId = supplierParamToId(params.supplierId);
    const bookingQueryVariables = createQueryVariables(
      "dashboard-pending-bookings",
      getBookingTableVariables({
        supplierId: supplierId,
        status: [
          BookingStatus.AwaitingConfirmation,
          BookingStatus.Accepted,
          BookingStatus.Vip,
        ],
        deliveryCityIds: [],
        departureCityIds: [],
        vehicles: [],
        relocationTypes: [],
      }),
      bookingTableDefaultSorting,
    );

    prefetchQuery(bookingListQuery, bookingQueryVariables, params.orgId);

    const relocationQueryVariables = createQueryVariables(
      "dashboard-relocations",
      getRelocationTableVariables({
        supplierId: supplierId,
        status: [RelocationStatus.Ready, RelocationStatus.Paused],
        // Must mirror RelocationMultiTable's `type` exactly — the query key is
        // [name, variables], so omitting it here makes the prefetch key differ
        // from the component's and the warm cache is missed (double fetch).
        type: [RelocationType.Relocation],
        regions: [],
      }),
      relocationTableDefaultSorting,
    );

    prefetchQuery(relocationListQuery, relocationQueryVariables, params.orgId);

    // Warm the table filter dropdowns + relocation tab counts. supplierId is
    // known from the URL, so these can run in parallel with everything above
    // instead of waterfalling once the table components mount. Each variable
    // object MUST match its component's call exactly (see OfficeFilter,
    // VehicleFilter, CityFilter, RelocationMultiTable Header) or the key won't
    // hit the warmed cache.
    const supplier_id = supplierId ?? undefined;

    prefetchQuery(
      relocationStatusCountsQuery,
      {
        supplierId: supplier_id,
        type: [RelocationType.Relocation],
      },
      params.orgId,
    );
    prefetchQuery(
      supplierOfficeListQuery,
      {
        first: 20,
        page: 1,
        archived: false,
        search: "",
        supplier_id,
        orderBy: [
          {
            order: SortOrder.Asc,
            column: QuerySupplierOfficesOrderByColumn.Name,
          },
        ],
      },
      params.orgId,
    );
    prefetchQuery(
      vehicleListQuery,
      {
        first: 20,
        page: 1,
        archived: false,
        search: "",
        supplier_id,
        orderBy: [
          { order: SortOrder.Asc, column: QueryVehiclesOrderByColumn.Name },
        ],
      },
      params.orgId,
    );
    prefetchQuery(
      cityListQuery,
      {
        search: "",
        first: 20,
        page: 1,
        region: undefined,
      },
      params.orgId,
    );

    // Admin-only dashboard queries (stats + WorkOrder blocks + sidebar badge).
    // isAdmin comes from the URL's tenant prefix (set in the app-layout
    // beforeLoad), so it's known synchronously here — these fire in the first
    // wave instead of waiting on the organisations query + a router.invalidate.
    if (params.tenant === "admin") {
      const { start, end } = getDatesFromFilterOption(
        TimelineFilterOption.SevenDays,
      );
      const startTz = toDateTimeTzString(start);
      const endTz = toDateTimeTzString(end);

      prefetchQuery(
        adminDashboardStatsQuery,
        {
          start: startTz,
          end: endTz,
          comparisonMode: ComparisonMode.PreviousYear,
        },
        params.orgId,
      );
      prefetchQuery(
        workOrderRegionalCountsQuery,
        { start: startTz, end: endTz },
        params.orgId,
      );
      prefetchQuery(workOrderStatusCountQuery, {}, params.orgId);
    }
  },
});
